import request from "../../utils/request";
import { Result } from "../../models/general";
import {
    UserSkillInput, UserSkillView, SkillView, SkillInput, SkillFilterInput, SkillInventoryView,
    SkillEvidenceView, UserStrengthView, UserStrengthEvidenceView, StrengthClusterView,
    StrengthClustersResultView, normalizeStrengthConfidence, normalizeSkillEvidenceSourceType,
} from "../../models/employee";

export async function getUserSkills(userId: string) {
    const response = await request.get<Result<UserSkillView[]>>(`Skill/GetUserSkills/${userId}`);
    return response.data;
}

export async function addUserSkill(input: UserSkillInput) {
    const response = await request.post<Result<UserSkillView>>("Skill/AddUserSkill", input);
    return response.data;
}

export async function editUserSkill(userSkillId: number, input: UserSkillInput) {
    const response = await request.post<Result<UserSkillView>>(`Skill/EditUserSkill/${userSkillId}`, input);
    return response.data;
}

export async function deleteUserSkill(userSkillId: number) {
    const response = await request.post<Result<UserSkillView[]>>(`Skill/DeleteUserSkill/${userSkillId}`);
    return response.data;
}

export async function createSkill(input: SkillInput) {
    const response = await request.post<Result<SkillView>>("Skill/CreateSkill", input);
    return response.data;
}

export async function searchSkills(searchTerm: string) {
    const response = await request.get<Result<SkillView[]>>(`Skill/SearchSkills?searchTerm=${encodeURIComponent(searchTerm)}`);
    return response.data;
}

export async function getCompanySkillsInventory(filter?: SkillFilterInput) {
    const response = await request.post<Result<SkillInventoryView[]>>(
        "Skill/GetCompanySkillsInventory",
        filter ?? {}
    );
    return response.data;
}

export async function getCompanyJobTitles(): Promise<Result<string[]>> {
    const response = await request.get<Result<string[]>>("Skill/GetCompanyJobTitles");
    return response.data;
}

export async function getSkillEvidence(userSkillId: number): Promise<Result<SkillEvidenceView[]>> {
    const response = await request.get<Result<SkillEvidenceView[]>>(`Skill/GetSkillEvidence/${userSkillId}`);
    return response.data;
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
   CO-447 / CO-448 / CO-449 — derived strengths, endorsement, suppression.

   🔴 The route is `Skill/GetUserStrengthEvidence/{userStrengthId}`, NOT `GetStrengthEvidence` as
   spec §3.3.1 writes it. The server took the safer name (`SkillController.cs:524`) precisely
   because `GetStrengthEvidence` sits one letter from the pre-existing
   `Skill/GetSkillEvidence/{userSkillId}` above, and the two take DIFFERENT ids (a strength id vs a
   user-skill id) — the wrong one would return a 200 full of somebody else's rows rather than an
   error, so the mistake would be silent. The function names here match that separation.
   ────────────────────────────────────────────────────────────────────────────────────────────── */

/** Every enum on the wire is a STRING; normalise anyway so a serializer change cannot break a chip. */
function normalizeStrength(raw: UserStrengthView): UserStrengthView {
    return { ...raw, confidence: normalizeStrengthConfidence(raw?.confidence) };
}

export async function getUserStrengths(userId: string): Promise<Result<UserStrengthView[]>> {
    const response = await request.get<Result<UserStrengthView[]>>(`Skill/GetUserStrengths/${userId}`);
    const data = response.data;
    return { ...data, payload: (data?.payload ?? []).map(normalizeStrength) };
}

export async function getUserStrengthEvidence(userStrengthId: number): Promise<Result<UserStrengthEvidenceView[]>> {
    const response = await request.get<Result<UserStrengthEvidenceView[]>>(
        `Skill/GetUserStrengthEvidence/${userStrengthId}`,
    );
    const data = response.data;
    return {
        ...data,
        payload: (data?.payload ?? []).map((e) => ({
            ...e,
            sourceType: normalizeSkillEvidenceSourceType(e?.sourceType),
        })),
    };
}

/**
 * Spec §3.3.1 types this endpoint as a bare `List<StrengthClusterView>`; §3.5.4 asks the SAME
 * endpoint to carry a `{ totalStrengths, suppressedStrengths, suppressionRate }` summary beside
 * the list. Which one W3-D shipped is not knowable from here, so accept BOTH and report which
 * arrived via `hasSuppressionSummary`. A client that assumed one shape would render an empty
 * clusters tab against the other, and an empty tab is exactly the state this step exists to end.
 */
export async function getCompanyStrengthClusters(): Promise<Result<StrengthClustersResultView>> {
    const response = await request.get<Result<unknown>>("Skill/GetCompanyStrengthClusters");
    const data = response.data;
    const raw = data?.payload as any;

    const rawClusters: any[] = Array.isArray(raw) ? raw : (raw?.clusters ?? []);
    const clusters: StrengthClusterView[] = rawClusters.map((c) => ({
        label: c?.label ?? '',
        peopleCount: c?.peopleCount ?? 0,
        departments: c?.departments ?? [],
        confidence: normalizeStrengthConfidence(c?.confidence),
        emergingTrend: !!c?.emergingTrend,
    }));

    const hasSuppressionSummary = !Array.isArray(raw) && typeof raw?.suppressedStrengths === 'number';
    const totalStrengths = typeof raw?.totalStrengths === 'number' ? raw.totalStrengths : clusters.length;
    const suppressedStrengths = hasSuppressionSummary ? raw.suppressedStrengths : 0;
    // 🔴 PERCENT, 0-100 — `CompanyStrengthClustersView.SuppressionRate` follows the same convention
    // as CoveragePercent / UtilizationPercent and is ALREADY multiplied by 100 server-side. Do not
    // multiply again. The derived fallback below is scaled to match, not left as a 0..1 fraction.
    const suppressionRate = typeof raw?.suppressionRate === 'number'
        ? raw.suppressionRate
        : (totalStrengths > 0 ? (suppressedStrengths / totalStrengths) * 100 : 0);

    return {
        ...data,
        payload: { clusters, totalStrengths, suppressedStrengths, suppressionRate, hasSuppressionSummary },
    };
}

/** CO-448. One endorsement per person per skill; the server refuses self-endorsement and duplicates. */
export async function endorseUserSkill(userSkillId: number, note?: string) {
    const response = await request.post<Result<UserSkillView>>(
        `Skill/EndorseUserSkill/${userSkillId}`,
        note ? { note } : {},
    );
    return response.data;
}

/** An endorsement you cannot take back is not a judgement, it is a ratchet. */
export async function withdrawEndorsement(userSkillId: number) {
    const response = await request.post<Result<UserSkillView>>(`Skill/WithdrawEndorsement/${userSkillId}`);
    return response.data;
}

/**
 * CO-449. 🔴 OWNER DECISION 6 (2026-08-27): authorised for the SUBJECT THEMSELF, the subject's
 * DIRECT MANAGER, and HR. NOT Admin, NOT Operator. Both halves changed: Admin was removed and the
 * subject was added. The server enforces it (`SkillService.LoadStrengthForSuppressionAsync`);
 * the UI gate (`canSuppressStrengths` in views/UserProfile/Skills) only mirrors it.
 */
export async function suppressStrength(userStrengthId: number, reason?: string) {
    const response = await request.post<Result<UserStrengthView>>(
        `Skill/Strength/${userStrengthId}/Suppress`,
        reason ? { reason } : {},
    );
    return response.data;
}

export async function unsuppressStrength(userStrengthId: number) {
    const response = await request.post<Result<UserStrengthView>>(`Skill/Strength/${userStrengthId}/Unsuppress`);
    return response.data;
}
